import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import { useCompetition } from '../context/CompetitionContext';

export default function Setup() {
  const { categories, setCategories, teams, setTeams } = useCompetition();
  const navigate = useNavigate();
  const location = useLocation();

  const [newCategoryName, setNewCategoryName] = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamLength, setNewTeamLength] = useState(180);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [editTeamName, setEditTeamName] = useState('');
  const [editTeamLength, setEditTeamLength] = useState(180);

  const addCategory = () => {
    if (newCategoryName.trim() === '') return;

    const category = {
      id: Date.now(),
      name: newCategoryName.trim()
    };

    setCategories([...categories, category]);
    setNewCategoryName('');
    if (!selectedCategory) {
      setSelectedCategory(category.id.toString());
    }
  };

  const deleteCategory = (id) => {
    if (!confirm('Delete this category and all its teams?')) return;

    setCategories(categories.filter(cat => cat.id !== id));
    setTeams(teams.filter(team => team.category_id !== id));
    if (Number(selectedCategory) === id) {
      setSelectedCategory(categories[0]?.id?.toString() || '');
    }
  };

  const addTeam = () => {
    if (newTeamName.trim() === '' || !selectedCategory) return;

    const team = {
      id: Date.now(),
      name: newTeamName.trim(),
      length: newTeamLength,
      category_id: Number(selectedCategory)
    };

    setTeams([...teams, team]);
    setNewTeamName('');
    setNewTeamLength(180);
  };

  const deleteTeam = (id) => {
    setTeams(teams.filter(team => team.id !== id));
  };

  const startEditTeam = (team) => {
    setEditingTeamId(team.id);
    setEditTeamName(team.name);
    setEditTeamLength(team.length);
  };

  const cancelEditTeam = () => {
    setEditingTeamId(null);
    setEditTeamName('');
    setEditTeamLength(180);
  };

  const saveEditTeam = () => {
    if (editTeamName.trim() === '') return;

    setTeams(teams.map(team =>
      team.id === editingTeamId
        ? { ...team, name: editTeamName.trim(), length: editTeamLength }
        : team
    ));
    setEditingTeamId(null);
    setEditTeamName('');
    setEditTeamLength(180);
  };

  const exportData = () => {
    const data = {
      categories,
      teams,
      exportDate: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dance-timer-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importData = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (data.categories && data.teams) {
          if (confirm('This will replace all current data. Continue?')) {
            setCategories(data.categories);
            setTeams(data.teams);
          }
        } else {
          alert('Invalid backup file');
        }
      } catch (error) {
        alert('Error reading backup file');
      }
    };
    reader.readAsText(file);
  };

  const clearAllData = () => {
    if (confirm('Delete ALL categories and teams? This cannot be undone!')) {
      setCategories([]);
      setTeams([]);
      setSelectedCategory('');
      localStorage.removeItem('dance-timer-categories');
      localStorage.removeItem('dance-timer-teams');
    }
  };

  return (
    <div className="h-screen max-h-screen bg-gray-100 flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-auto p-4 flex justify-center items-start">
        <div className="bg-white rounded-lg shadow-lg p-4 max-w-2xl w-full h-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-800">
              Competition Setup
            </h1>
          </div>

        {/* Data Management */}
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={exportData}
              className="w-full px-4 py-3 bg-blue-500 text-white text-sm font-semibold rounded hover:bg-blue-600 flex flex-col items-center gap-1"
            >
              <span className="text-xl">📥</span>
              <span>Export Backup</span>
            </button>
            <label className="w-full px-4 py-3 bg-green-500 text-white text-sm font-semibold rounded hover:bg-green-600 cursor-pointer flex flex-col items-center gap-1">
              <span className="text-xl">📤</span>
              <span>Import Backup</span>
              <input
                type="file"
                accept=".json"
                onChange={importData}
                className="hidden"
              />
            </label>
            <button
              onClick={clearAllData}
              className="w-full px-4 py-3 bg-red-500 text-white text-sm font-semibold rounded hover:bg-red-600 flex flex-col items-center gap-1"
            >
              <span className="text-xl">🗑️</span>
              <span>Clear All Data</span>
            </button>
          </div>
        </div>

        {/* Category Management */}
        <div className="mb-8 pb-6 border-b-2 border-gray-200">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Step 1: Add Categories</h2>

          <div className="flex flex-wrap gap-3 mb-4">
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addCategory()}
              placeholder="Category name (e.g., Junior Solo, MegaCrew)"
              className="flex-1 px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            />
            <button
              onClick={addCategory}
              className="px-6 py-3 bg-purple-600 text-white font-bold rounded-lg hover:bg-purple-700 transition-colors"
            >
              Add Category
            </button>
          </div>

          {categories.length > 0 && (
            <div className="space-y-2">
              {categories.map(category => (
                <div key={category.id} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <span className="font-semibold text-gray-800">{category.name}</span>
                  <button
                    onClick={() => deleteCategory(category.id)}
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm"
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Team Management */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">Step 2: Add Teams</h2>

          {categories.length === 0 ? (
            <p className="text-gray-500 italic mb-4">Add at least one category first</p>
          ) : (
            <div className="flex flex-col gap-3 mb-4">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              >
                <option value="">Select category...</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>

              <input
                type="text"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addTeam()}
                placeholder="Team name"
                className="px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                disabled={!selectedCategory}
              />

              <div className="flex flex-wrap gap-3">
                <input
                  type="number"
                  value={newTeamLength}
                  onChange={(e) => setNewTeamLength(parseInt(e.target.value) || '')}
                  onBlur={(e) => setNewTeamLength(Math.max(60, parseInt(e.target.value) || 60))}
                  onFocus={(e) => e.target.select()}
                  placeholder="Length (seconds)"
                  className="flex-1 px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
                  min="60"
                  disabled={!selectedCategory}
                />
                <button
                  onClick={addTeam}
                  disabled={!selectedCategory}
                  className={`px-6 py-3 font-bold rounded-lg transition-colors ${
                    selectedCategory
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Add Team
                </button>
              </div>
              <p className="text-sm text-gray-600">
                {Math.floor(newTeamLength / 60)}:{(newTeamLength % 60).toString().padStart(2, '0')} minutes
              </p>
            </div>
          )}
        </div>

        {teams.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Teams Added ({teams.length})</h3>
            <div className="space-y-3 max-h-[32rem] overflow-y-auto">
              {categories.map(category => {
                const categoryTeams = teams.filter(team => team.category_id === category.id);
                if (categoryTeams.length === 0) return null;

                return (
                  <div key={category.id} className="mb-3">
                    <div className="text-sm font-bold text-purple-600 mb-2">{category.name}</div>
                    {categoryTeams.map(team => (
                      <div key={team.id} className="p-3 bg-gray-50 rounded-lg mb-2">
                        {editingTeamId === team.id ? (
                          // Edit mode
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={editTeamName}
                              onChange={(e) => setEditTeamName(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && saveEditTeam()}
                              className="w-full px-3 py-2 border-2 border-blue-300 rounded focus:outline-none focus:border-blue-500"
                              placeholder="Team name"
                            />
                            <div className="flex gap-2 items-center">
                              <input
                                type="number"
                                value={editTeamLength}
                                onChange={(e) => setEditTeamLength(parseInt(e.target.value) || '')}
                                onBlur={(e) => setEditTeamLength(Math.max(60, parseInt(e.target.value) || 60))}
                                onFocus={(e) => e.target.select()}
                                className="flex-1 px-3 py-2 border-2 border-blue-300 rounded focus:outline-none focus:border-blue-500"
                                min="60"
                              />
                              <span className="text-sm text-gray-600">
                                ({Math.floor(editTeamLength / 60)}:{(editTeamLength % 60).toString().padStart(2, '0')})
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={saveEditTeam}
                                className="px-4 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-sm"
                              >
                                Save
                              </button>
                              <button
                                onClick={cancelEditTeam}
                                className="px-4 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          // Display mode
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-semibold text-gray-800">{team.name}</span>
                              <span className="text-gray-600 ml-3">
                                ({Math.floor(team.length / 60)}:{(team.length % 60).toString().padStart(2, '0')})
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => startEditTeam(team)}
                                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => deleteTeam(team.id)}
                                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </div>
      </div>

      <BottomNav variant="setup" />
    </div>
  );
}
